import { useState, useCallback, useEffect } from 'react';
import type { DateRange } from 'react-day-picker';
import {
    fetchAdminPickerStats,
    fetchAdminPickerDetail,
    type PickerStatsSummary,
    type PickerSessionDetail,
} from '../services/pickerStatsService';
import { getErrorMessage } from '../utils/errors';

export function usePickerStats() {
    const [dateRange, setDateRange] = useState<DateRange | undefined>({
        from: new Date(new Date(new Date().setDate(new Date().getDate() - 29)).setHours(0, 0, 0, 0)),
        to:   new Date(),
    });
    const [pickers,       setPickers]       = useState<PickerStatsSummary[]>([]);
    const [loading,       setLoading]       = useState(true);
    const [error,         setError]         = useState<string | null>(null);
    const [selectedPicker, setSelectedPicker] = useState<PickerStatsSummary | null>(null);
    const [detailSessions, setDetailSessions] = useState<PickerSessionDetail[]>([]);
    const [detailLoading,  setDetailLoading]  = useState(false);

    const startDate = dateRange?.from
        ? new Date(Date.UTC(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate(), 0, 0, 0, 0)).toISOString()
        : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();

    const endDate = dateRange?.to
        ? new Date(Date.UTC(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59, 999)).toISOString()
        : new Date(new Date().setHours(23, 59, 59, 999)).toISOString();

    const loadOverview = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setPickers(await fetchAdminPickerStats(startDate, endDate));
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load picker stats'));
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => { void loadOverview(); }, [loadOverview]);

    const openDetail = useCallback(async (picker: PickerStatsSummary) => {
        setSelectedPicker(picker);
        setDetailLoading(true);
        try {
            setDetailSessions(await fetchAdminPickerDetail(picker.picker_id, startDate, endDate));
        } catch {
            setDetailSessions([]);
        } finally {
            setDetailLoading(false);
        }
    }, [startDate, endDate]);

    return {
        dateRange, setDateRange,
        pickers, loading, error,
        selectedPicker, setSelectedPicker,
        detailSessions, detailLoading,
        openDetail,
    };
}